import { useSearch } from "contexts/search/SearchContextProvider";
import { Period } from "hooks/periodIndex";
import {
  Loaded,
  liftLoadedState,
  mapLoaded,
  mapLoadedUnpack,
} from "models/loaded";
import { ProductionPlan } from "models/ProductionPlan";
import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router";
import {
  InventoryItem,
  MaterialChemistry,
  MaterialLimit,
  MiscParams,
  ObtainableBundleItem,
  PhysicalParameters,
  SearchPeriod,
  SearchPeriodProductionPlan,
  TargetBasketItem,
  TargetInventoryItem,
} from "src/store/api/generatedApi";
import type { SearchRead } from "src/store/api/generatedApi";
import type { SyncedContextData } from "contexts/search/context";
import { convertSearchPeriodProductionPlan } from "src/utils/convertSearchPeriodProductionPlan";
import { mergeMaterialLimits } from "src/utils/materialLimitOverrides";

export const useSessionId = (sessionId?: number): number | null => {
  const urlSessionId = parseInt(useSearchParams()[0].get("session") ?? "");
  if (typeof sessionId === "number") {
    return sessionId;
  } else if (typeof urlSessionId === "number" && !isNaN(urlSessionId)) {
    return urlSessionId;
  } else {
    return null;
  }
};

export const useSearchId = (searchId?: number): number | null => {
  const urlSearchId = parseInt(useSearchParams()[0].get("search") ?? "");
  if (typeof searchId === "number") {
    return searchId;
  } else if (typeof urlSearchId === "number" && !isNaN(urlSearchId)) {
    return urlSearchId;
  } else {
    return null;
  }
};

export const usePlanId = (): number | null => {
  const urlPlanId = parseInt(useSearchParams()[0].get("plan") ?? "");
  if (typeof urlPlanId === "number" && !isNaN(urlPlanId)) {
    return urlPlanId;
  } else {
    return null;
  }
};

const blankPeriod: SearchPeriod = {
  name: null,
  is_deployable: false,
  start_timestamp: null,
  end_timestamp: null,
  electrical_energy_price: 0,
  production_plan: { product_group_items: null, steel_grade_items: [] },
  obtainable_bundles: [],
  target_inventory: [],
  target_baskets: [],
  material_exclusivity: [],
  suppress_mix_material_exclusivity_constraints: false,
  suppress_min_feasible_mass_constraints: false,
  optimisation_objective_weighting: 1.0,
};

export const useSearchName = (): Loaded<string> =>
  mapLoaded(useSearch().parameters.client, ({ session_name }) => session_name);

export const useRunSearch = () => {
  const {
    results: { run },
    parameters: { server },
  } = useSearch();
  const serverSearchId = mapLoadedUnpack(server, ({ id }) => id);

  const [requested, setRequested] = useState(false);
  const ready = serverSearchId != null;

  // Wait for syncing to finish before running the search
  useEffect(() => {
    if (requested && ready) {
      // eslint-disable-next-line @eslint-react/set-state-in-effect -- state machine: requested+ready triggers run
      setRequested(false);
      run(serverSearchId);
    }
    // eslint-disable-next-line @eslint-react/exhaustive-deps -- run and serverSearchId change on every render; only gate on the flags that indicate readiness
  }, [requested, ready]);

  return () => {
    setRequested(true);
  };
};

export const useMiscParams = () => {
  const { client, setClient } = useSearch().parameters;
  return [
    mapLoaded(client, ({ misc_params }) => misc_params),
    (update: (miscParams: MiscParams) => MiscParams) => {
      setClient((current) => ({
        ...current,
        misc_params: update(current.misc_params),
      }));
    },
  ] as const;
};

export const usePhysicalParameters = () => {
  const { client, setClient } = useSearch().parameters;
  return [
    mapLoaded(client, ({ physical_parameters }) => physical_parameters),
    (
      update: (physicalParameters: PhysicalParameters) => PhysicalParameters
    ) => {
      setClient((current) => ({
        ...current,
        physical_parameters: update(current.physical_parameters),
      }));
    },
  ] as const;
};

export const useInventory = () => {
  const { client, setClient } = useSearch().parameters;
  return [
    mapLoaded(client, ({ inventory }) => inventory),
    (update: (inventory: InventoryItem[]) => InventoryItem[]) => {
      setClient((current) => ({
        ...current,
        inventory: update(current.inventory),
      }));
    },
  ] as const;
};

export const useMaterialChemistryOverrides = () => {
  const { client, setClient } = useSearch().parameters;
  return [
    mapLoaded(
      client,
      ({ material_chemistry_overrides }) => material_chemistry_overrides
    ),
    (update: (overrides: MaterialChemistry[]) => MaterialChemistry[]) => {
      setClient((current) => ({
        ...current,
        material_chemistry_overrides: update(
          current.material_chemistry_overrides
        ),
      }));
    },
  ] as const;
};

export const useMaterialLimits = () => {
  const { client, setClient } = useSearch().parameters;
  const context = useSearch().context;

  return {
    materialLimitOverrides: mapLoaded<SearchRead, MaterialLimit[]>(
      client,
      ({ material_limit_overrides }) => material_limit_overrides
    ),
    baseMaterialLimits: mapLoaded<SyncedContextData, MaterialLimit[]>(
      context,
      ({ materialLimits }) => materialLimits
    ),
    materialLimits: mapLoaded<
      { base: SyncedContextData; params: SearchRead },
      MaterialLimit[]
    >(
      liftLoadedState({
        base: context,
        params: client,
      }),
      ({ base, params }) =>
        mergeMaterialLimits(
          base.materialLimits,
          params.material_limit_overrides
        )
    ),
    setMaterialLimitOverrides: useCallback(
      (update: (materialLimits: MaterialLimit[]) => MaterialLimit[]) => {
        setClient((current) => ({
          ...current,
          material_limit_overrides: update(current.material_limit_overrides),
        }));
      },
      [setClient]
    ),
  } as const;
};

export const useTargetBaskets = (period: Period) => {
  const { client, setClient } = useSearch().parameters;
  const periodIndex = period - 1;
  return [
    mapLoaded(
      client,
      ({ periods }) => periods[periodIndex]?.target_baskets ?? []
    ),
    (update: (targetBaskets: TargetBasketItem[]) => TargetBasketItem[]) => {
      setClient((current) => ({
        ...current,
        periods: current.periods.map((period) => ({
          ...period,
          target_baskets: update(period.target_baskets),
        })),
      }));
    },
  ] as const;
};

export const usePeriod = (period: Period) => {
  const { client, setClient } = useSearch().parameters;
  const periodIndex = period - 1;
  return [
    mapLoaded(client, ({ periods }) => periods[periodIndex] ?? blankPeriod),
    (update: (period: SearchPeriod) => SearchPeriod) => {
      setClient((current) => {
        const periods = [...current.periods];
        periods[periodIndex] = update(
          current.periods[periodIndex] ?? blankPeriod
        );
        return { ...current, periods };
      });
    },
  ] as const;
};

export const useProductionSchedule = () => {
  const [period, setPeriod] = usePeriod(1 as Period);
  return [
    mapLoaded(period, ({ production_plan }) => production_plan),
    (
      update: (
        productionPlan: SearchPeriodProductionPlan
      ) => SearchPeriodProductionPlan
    ) => {
      setPeriod((current) => ({
        ...current,
        production_plan: update(current.production_plan),
      }));
    },
  ] as const;
};

export const useProductionPlan = (periodIndex: Period) => {
  const [period, setPeriod] = usePeriod(periodIndex);

  const convertToBackendFormat = (
    productionPlan: ProductionPlan
  ): SearchPeriodProductionPlan =>
    productionPlan.type === "steelGrade"
      ? { steel_grade_items: productionPlan.items, product_group_items: null }
      : productionPlan.type === "productGroup"
        ? { steel_grade_items: null, product_group_items: productionPlan.items }
        : {
            steel_grade_items: null,
            product_group_items: null,
          };

  return [
    mapLoaded(period, ({ production_plan }) =>
      convertSearchPeriodProductionPlan(production_plan)
    ),
    (update: (productionPlan: ProductionPlan) => ProductionPlan) => {
      setPeriod((current) => ({
        ...current,
        production_plan: convertToBackendFormat(
          update(convertSearchPeriodProductionPlan(current.production_plan))
        ),
      }));
    },
  ] as const;
};

export const useObtainableBundles = (periodIndex: Period) => {
  const [period, setPeriod] = usePeriod(periodIndex);
  return [
    mapLoaded(period, ({ obtainable_bundles }) => obtainable_bundles),
    (
      update: (
        obtainableBundles: ObtainableBundleItem[]
      ) => ObtainableBundleItem[]
    ) => {
      setPeriod((current) => ({
        ...current,
        obtainable_bundles: update(current.obtainable_bundles),
      }));
    },
  ] as const;
};

export const useTargetInventory = (period: Period) => {
  const [searchPeriod, setSearchPeriod] = usePeriod(period);
  return [
    mapLoaded(searchPeriod, ({ target_inventory }) => target_inventory),
    (
      update: (targetInventory: TargetInventoryItem[]) => TargetInventoryItem[]
    ) => {
      setSearchPeriod((current) => ({
        ...current,
        target_inventory: update(current.target_inventory),
      }));
    },
  ] as const;
};

export const useServerIds = () => {
  const search = useSearch().parameters.server;
  return {
    searchId: mapLoadedUnpack(search, ({ id }) => id),
    sessionId: mapLoadedUnpack(search, ({ session_id }) => session_id),
  };
};

export const usePeriods = () => {
  const { client, setClient } = useSearch().parameters;
  return [
    mapLoaded(client, ({ periods }) => periods),
    (update: (periods: SearchPeriod[]) => SearchPeriod[]) => {
      setClient((current) => ({
        ...current,
        periods: update(current.periods),
      }));
    },
  ] as const;
};
